import {
  connectorPoints,
  getBoundWithRotation,
  hitPolyline,
  resolveEndpoint,
  ropePoints,
} from './geometry';
import { SpatialIndex } from './SpatialIndex';
import type {
  BrushProps,
  ConnectorEndpoint,
  ElementType,
  GfxElement,
  IBound,
  IVec,
} from './types';
import { boundOf } from './types';

export type SurfaceEvent =
  | { kind: 'add'; id: string }
  | { kind: 'update'; id: string; patch: Partial<GfxElement> }
  | { kind: 'remove'; id: string }
  | { kind: 'change' };

type Listener = (event: SurfaceEvent) => void;

/** 元素的可交互外扩（借鉴 AFFiNE 的 responseExtension）。 */
export function responseExtension(el: GfxElement): IVec {
  if (el.type === 'connector' || el.type === 'rope') return [12, 12];
  if (el.type === 'pin' || el.type === 'brush' || el.type === 'stamp') return [12, 12];
  return [8, 8];
}

/** 手绘点列的绝对坐标。 */
export function brushPoints(el: GfxElement): IVec[] {
  const props = el.props as unknown as BrushProps;
  const pts = Array.isArray(props.points) ? props.points : [];
  return pts.map(p => [el.x + p[0], el.y + p[1]] as IVec);
}

/**
 * 无限画布数据模型（借鉴 AFFiNE SurfaceBlockModel 的语义）：
 * 类型化元素容器 + 空间索引 + 分组层级 + 本地瞬时元素 + 细粒度事件。
 */
export class SurfaceModel {
  private elements = new Map<string, GfxElement>();
  private groups = new Map<string, string>(); // childId -> groupId
  private attachments = new Map<string, Set<string>>(); // hostId -> childIds
  private localIds = new Set<string>();
  private index = new SpatialIndex();
  private listeners = new Set<Listener>();
  private dirty = false;

  // ── 事件 ──────────────────────────────────────────────
  on(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private emit(event: SurfaceEvent): void {
    for (const fn of this.listeners) fn(event);
  }

  /** 标记需要重绘（合并大量更新时使用）。 */
  markDirty(): void {
    if (this.dirty) return;
    this.dirty = true;
    queueMicrotask(() => {
      this.dirty = false;
      this.emit({ kind: 'change' });
    });
  }

  // ── 查询 ──────────────────────────────────────────────
  get(id: string): GfxElement | undefined {
    return this.elements.get(id);
  }

  lookup = (id: string): GfxElement | undefined => this.elements.get(id);

  has(id: string): boolean {
    return this.elements.has(id);
  }

  all(): GfxElement[] {
    return [...this.elements.values()];
  }

  byType(type: ElementType): GfxElement[] {
    return this.all().filter(el => el.type === type);
  }

  get size(): number {
    return this.elements.size;
  }

  /** 按 z 排序（升序，越小越靠下）。 */
  sorted(): GfxElement[] {
    return this.all().sort((a, b) => (a.z < b.z ? -1 : a.z > b.z ? 1 : 0));
  }

  get elementsBound(): IBound | null {
    const bounds = this.all().map(el => getBoundWithRotation(el));
    if (bounds.length === 0) return null;
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const b of bounds) {
      minX = Math.min(minX, b.x);
      minY = Math.min(minY, b.y);
      maxX = Math.max(maxX, b.x + b.w);
      maxY = Math.max(maxY, b.y + b.h);
    }
    return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
  }

  /** 视口查询：返回与包围盒相交的元素，按 z 升序。 */
  query(bound: IBound): GfxElement[] {
    const ids = this.index.search(bound);
    const result: GfxElement[] = [];
    for (const id of ids) {
      const el = this.elements.get(id);
      if (!el || el.hidden) continue;
      result.push(el);
    }
    return result.sort((a, b) => (a.z < b.z ? -1 : 1));
  }

  /** 命中测试：返回最上层命中的元素。 */
  hitTest(point: IVec, options: { tolerance?: number; includeConnectors?: boolean } = {}): GfxElement | null {
    const tolerance = options.tolerance ?? 8;
    const candidates = this.sorted().reverse(); // 从上到下
    for (const el of candidates) {
      if (el.hidden) continue;
      if (el.type === 'connector' || el.type === 'rope') {
        if (options.includeConnectors === false) continue;
        const props = el.props as {
          source?: ConnectorEndpoint;
          target?: ConnectorEndpoint;
          mode?: 'straight' | 'orth';
          sag?: number;
        };
        if (!props.source || !props.target) continue;
        const points =
          el.type === 'rope'
            ? ropePoints(
                resolveEndpoint(props.source, this.lookup),
                resolveEndpoint(props.target, this.lookup),
                props.sag ?? 0.12
              )
            : connectorPoints(props.source, props.target, props.mode ?? 'straight', this.lookup);
        if (hitPolyline(point, points, tolerance + (el.type === 'rope' ? 3 : 0))) return el;
        continue;
      }
      if (el.type === 'brush') {
        const points = brushPoints(el);
        if (points.length >= 2 && hitPolyline(point, points, tolerance)) return el;
        continue;
      }
      const b = boundOf(el);
      const ext = responseExtension(el);
      if (
        point[0] >= b.x - ext[0] &&
        point[0] <= b.x + b.w + ext[0] &&
        point[1] >= b.y - ext[1] &&
        point[1] <= b.y + b.h + ext[1]
      ) {
        return el;
      }
    }
    return null;
  }

  /** 框选：返回与包围盒相交的元素。 */
  elementsInBound(bound: IBound): GfxElement[] {
    return this.query(bound).filter(el => !el.hidden);
  }

  // ── 变更 ──────────────────────────────────────────────
  add(element: GfxElement): GfxElement {
    this.elements.set(element.id, element);
    this.reindex(element);
    if (element.local) this.localIds.add(element.id);
    this.indexAttachment(element.id, element.attachTo ?? null);
    this.emit({ kind: 'add', id: element.id });
    return element;
  }

  update(id: string, patch: Partial<GfxElement>): GfxElement | null {
    const current = this.elements.get(id);
    if (!current) return null;
    const next = { ...current, ...patch, props: patch.props ? { ...patch.props } : current.props };
    this.elements.set(id, next);

    if ('attachTo' in patch) this.indexAttachment(id, next.attachTo ?? null);

    const moved = 'x' in patch || 'y' in patch;
    if (moved || 'w' in patch || 'h' in patch || 'rotate' in patch || 'props' in patch) {
      this.reindex(next);
      // 连接线 / 绳索依赖端点元素
      if (current.type !== 'connector' && current.type !== 'rope') this.reindexConnectorsFor(id);
      // 宿主移动时，随附装饰（图钉/胶带等）同步位移
      if (moved) {
        const dx = next.x - current.x;
        const dy = next.y - current.y;
        if (dx !== 0 || dy !== 0) this.propagateAttachments(id, dx, dy);
      }
    }
    this.emit({ kind: 'update', id, patch });
    return next;
  }

  private indexAttachment(childId: string, hostId: string | null): void {
    for (const [host, children] of this.attachments) {
      if (children.delete(childId) && children.size === 0) this.attachments.delete(host);
    }
    if (!hostId) return;
    const set = this.attachments.get(hostId) ?? new Set<string>();
    set.add(childId);
    this.attachments.set(hostId, set);
  }

  private propagateAttachments(hostId: string, dx: number, dy: number): void {
    const children = this.attachments.get(hostId);
    if (!children) return;
    for (const childId of [...children]) {
      const child = this.elements.get(childId);
      if (!child) continue;
      const next = { ...child, x: child.x + dx, y: child.y + dy };
      this.elements.set(childId, next);
      this.reindex(next);
      this.reindexConnectorsFor(childId);
      this.emit({ kind: 'update', id: childId, patch: { x: next.x, y: next.y } });
      // 子项若也有随附物，继续下推一层
      this.propagateAttachments(childId, dx, dy);
    }
  }

  /** 某元素上随附的装饰元素。 */
  attachmentsOf(hostId: string): GfxElement[] {
    return [...(this.attachments.get(hostId) ?? [])]
      .map(id => this.elements.get(id))
      .filter(Boolean) as GfxElement[];
  }

  remove(id: string): boolean {
    const el = this.elements.get(id);
    if (!el) return false;
    this.elements.delete(id);
    this.index.remove(id);
    this.localIds.delete(id);
    this.groups.delete(id);
    // 随附装饰随宿主一起移除
    for (const childId of [...(this.attachments.get(id) ?? [])]) {
      this.remove(childId);
    }
    this.attachments.delete(id);
    // 清理连接线 / 绳索
    for (const other of this.elements.values()) {
      if (other.type !== 'connector' && other.type !== 'rope') continue;
      const props = other.props as { source?: ConnectorEndpoint; target?: ConnectorEndpoint };
      if (props.source?.id === id || props.target?.id === id) {
        this.elements.delete(other.id);
        this.index.remove(other.id);
        this.emit({ kind: 'remove', id: other.id });
      }
    }
    this.emit({ kind: 'remove', id });
    return true;
  }

  clear(): void {
    this.elements.clear();
    this.index.clear();
    this.groups.clear();
    this.attachments.clear();
    this.localIds.clear();
    this.emit({ kind: 'change' });
  }

  /** 批量替换（加载时使用），不触发多次重绘。 */
  replaceAll(elements: GfxElement[]): void {
    this.elements.clear();
    this.index.clear();
    this.groups.clear();
    this.attachments.clear();
    this.localIds.clear();
    for (const el of elements) {
      this.elements.set(el.id, el);
      this.reindex(el);
      if (el.local) this.localIds.add(el.id);
      if (el.group) this.groups.set(el.id, el.group);
    }
    // 宿主索引在全部元素就位后建立
    for (const el of elements) this.indexAttachment(el.id, el.attachTo ?? null);
    this.emit({ kind: 'change' });
  }

  // ── 空间索引 ──────────────────────────────────────────
  private boundForIndex(el: GfxElement): IBound {
    if (el.type === 'brush') {
      const points = brushPoints(el);
      if (points.length > 0) {
        const xs = points.map(p => p[0]);
        const ys = points.map(p => p[1]);
        const minX = Math.min(...xs);
        const minY = Math.min(...ys);
        return {
          x: minX - 14,
          y: minY - 14,
          w: Math.max(...xs) - minX + 28,
          h: Math.max(...ys) - minY + 28,
        };
      }
    }
    if (el.type === 'connector' || el.type === 'rope') {
      const props = el.props as {
        source?: ConnectorEndpoint;
        target?: ConnectorEndpoint;
        mode?: 'straight' | 'orth';
        sag?: number;
      };
      if (props.source && props.target) {
        const points =
          el.type === 'rope'
            ? ropePoints(
                resolveEndpoint(props.source, this.lookup),
                resolveEndpoint(props.target, this.lookup),
                props.sag ?? 0.12
              )
            : connectorPoints(props.source, props.target, props.mode ?? 'straight', this.lookup);
        const xs = points.map(p => p[0]);
        const ys = points.map(p => p[1]);
        const minX = Math.min(...xs);
        const minY = Math.min(...ys);
        return {
          x: minX - 16,
          y: minY - 16,
          w: Math.max(...xs) - minX + 32,
          h: Math.max(...ys) - minY + 32,
        };
      }
      return { x: el.x - 16, y: el.y - 16, w: 32, h: 32 };
    }
    const b = getBoundWithRotation(el);
    const ext = responseExtension(el);
    return { x: b.x - ext[0], y: b.y - ext[1], w: b.w + ext[0] * 2, h: b.h + ext[1] * 2 };
  }

  private reindex(el: GfxElement): void {
    this.index.insert(el.id, this.boundForIndex(el));
  }

  /** 端点元素移动后，重算相关连接线 / 绳索的索引。 */
  reindexConnectorsFor(elementId: string): void {
    for (const el of this.elements.values()) {
      if (el.type !== 'connector' && el.type !== 'rope') continue;
      const props = el.props as { source?: ConnectorEndpoint; target?: ConnectorEndpoint };
      if (props.source?.id === elementId || props.target?.id === elementId) {
        this.reindex(el);
      }
    }
  }

  // ── 分组 ──────────────────────────────────────────────
  groupOf(id: string): string | undefined {
    return this.groups.get(id);
  }

  childrenOf(groupId: string): GfxElement[] {
    return this.all().filter(el => this.groups.get(el.id) === groupId);
  }

  setGroup(childId: string, groupId: string | null): void {
    if (groupId) this.groups.set(childId, groupId);
    else this.groups.delete(childId);
    this.update(childId, { group: groupId });
  }

  // ── 本地元素 ──────────────────────────────────────────
  isLocal(id: string): boolean {
    return this.localIds.has(id);
  }

  localElements(): GfxElement[] {
    return this.all().filter(el => this.localIds.has(el.id));
  }

  /** 清除所有本地元素（如取消一次拖拽/绘制）。 */
  clearLocal(): void {
    for (const id of [...this.localIds]) this.remove(id);
  }
}